import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, API } from "@/lib/apiClient";
import { AppHeader } from "@/components/AppHeader";
import { CodeEditor } from "@/components/CodeEditor";
import { MathTools, makeInsertHandler } from "@/components/MathTools";
import { MicButton } from "@/components/MicButton";
import { ReadAloudButton } from "@/components/ReadAloudButton";
import { renderMarkdown } from "@/lib/renderMarkdown";
import { toast } from "sonner";
import { useSubscription } from "@/context/SubscriptionContext";
import {
  ArrowLeft,
  Send,
  Sparkles,
  Bot,
  User as UserIcon,
  Loader2,
  History,
  Presentation,
  Code2,
} from "lucide-react";


export default function ChatPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { showLock } = useSubscription();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const scrollerRef = useRef(null);
  const chatInputRef = useRef(null);

  // Detect default playground language from the chapter context.
  const subject = (session?.subject || "").toLowerCase();
  const topicId = session?.topic_id || "";
  const isComputer = subject.startsWith("computer");
  // Python chapters are comp-11-9..14 and comp-12-9..12 (see syllabus_data.py).
  const isPythonChapter = (() => {
    const m = topicId.match(/^comp-(11|12)-(\d+)$/);
    if (!m) return false;
    const cls = m[1], num = parseInt(m[2], 10);
    if (cls === "11" && num >= 9 && num <= 14) return true;
    if (cls === "12" && num >= 9 && num <= 12) return true;
    return false;
  })();
  const playgroundLang = isPythonChapter ? "python" : "java";
  const showPlayground = isComputer;

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/chat/sessions/${sessionId}/messages`),
      api.get(`/chat/sessions`),
    ])
      .then(([msgRes, sessRes]) => {
        setSession(msgRes.data.session);
        setMessages(msgRes.data.messages);
        setHistory(sessRes.data);
      })
      .catch(() => toast.error("Failed to load chat"))
      .finally(() => setLoading(false));
  }, [sessionId]);

  useEffect(() => {
    if (scrollerRef.current)
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, streaming]);

  const sendMessage = async (overrideText) => {
    const useOverride = typeof overrideText === "string";
    const text = (useOverride ? overrideText : input).trim();
    if (!text || streaming) return;
    if (!useOverride) setInput("");
    const tempUser = {
      id: `tmp-u-${Date.now()}`,
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    const tempAi = {
      id: `tmp-a-${Date.now()}`,
      role: "assistant",
      content: "",
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, tempUser, tempAi]);
    setStreaming(true);

    try {
      const token = localStorage.getItem("brimi_token");
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (res.status === 402) {
        showLock("Your free trial has ended. Subscribe to keep chatting with Brimi.");
        setMessages((m) => m.slice(0, -2));
        return;
      }
      if (!res.ok || !res.body) {
        throw new Error(`HTTP ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          try {
            const ev = JSON.parse(payload);
            if (ev.type === "delta") {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  ...copy[copy.length - 1],
                  content: copy[copy.length - 1].content + ev.content,
                };
                return copy;
              });
            } else if (ev.type === "error") {
              toast.error(ev.content || "AI error");
            }
          } catch (e) {
            // Swallow JSON.parse on incomplete SSE chunk; next iteration will retry with full buffer.
            if (process.env.NODE_ENV === "development") {
              // eslint-disable-next-line no-console
              console.debug("SSE parse skipped:", e?.message);
            }
          }
        }
      }
    } catch (e) {
      toast.error("Failed to get response");
      setMessages((m) => m.slice(0, -1));
    } finally {
      setStreaming(false);
    }
  };

  const startNewChat = async () => {
    if (!session?.topic_id) return;
    try {
      const { data } = await api.post("/chat/start", {
        topic_id: session.topic_id,
      });
      navigate(`/tutor/${data.session_id}`);
    } catch {
      toast.error("Couldn't start new chat");
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <AppHeader />
      <div className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-[280px_1fr] gap-6">
        {/* History sidebar */}
        <aside
          className="hidden lg:flex flex-col tactile-card p-4 h-[calc(100vh-7rem)] sticky top-20"
          data-testid="chat-history-sidebar"
        >
          <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-muted-foreground mb-3">
            <History className="w-4 h-4" /> History
          </div>
          <div className="space-y-2 overflow-y-auto pr-1">
            {history.length === 0 && (
              <div className="text-sm text-muted-foreground">
                No past chats yet.
              </div>
            )}
            {history.map((s) => (
              <Link
                key={s.id}
                to={`/tutor/${s.id}`}
                data-testid={`history-item-${s.id}`}
                className={`block rounded-2xl px-3 py-2.5 text-sm border transition ${
                  s.id === sessionId
                    ? "bg-[hsl(var(--brimi-blue))]/10 border-[hsl(var(--brimi-blue))]/30 text-foreground"
                    : "bg-card border-transparent hover:border-border hover:bg-muted"
                }`}
              >
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-0.5">
                  {s.subject} · {s.class_id}
                </div>
                <div className="font-semibold truncate">{s.topic_title}</div>
              </Link>
            ))}
          </div>
        </aside>

        {/* Chat panel */}
        <section className="flex flex-col tactile-card overflow-hidden h-[calc(100vh-12rem)] sm:h-[calc(100vh-10rem)]">
          <div className="flex items-center justify-between gap-3 p-4 border-b border-border bg-gradient-to-r from-[hsl(var(--brimi-blue))]/5 to-[hsl(var(--brimi-coral))]/5">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => navigate(-1)}
                data-testid="chat-back-btn"
                className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-foreground/5"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
                  {session?.subject} · Class {session?.class_id}
                </div>
                <div className="font-bold text-base sm:text-lg truncate">
                  {session?.topic_title || "Loading…"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {session?.topic_id && (
                <Link
                  data-testid="chat-notes-tab"
                  to={`/materials/${session.topic_id}`}
                  className="hidden sm:inline-flex pill-btn bg-[hsl(var(--brimi-mint))] hover:bg-[hsl(var(--brimi-mint))]/90 text-white text-xs px-3 py-2"
                  title="Course Material"
                >
                  <Presentation className="w-3.5 h-3.5" /> Notes
                </Link>
              )}
              <button
                data-testid="chat-new-btn"
                onClick={startNewChat}
                className="hidden sm:inline-flex pill-btn bg-foreground/5 hover:bg-foreground/10 text-xs px-3 py-2"
              >
                <Sparkles className="w-3.5 h-3.5" /> New chat
              </button>
            </div>
          </div>

          <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
            {loading && (
              <div className="text-muted-foreground text-sm">Loading…</div>
            )}
            {!loading && messages.length === 0 && (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[hsl(var(--brimi-blue))]/10 flex items-center justify-center">
                  <Bot className="w-8 h-8 text-[hsl(var(--brimi-blue))]" />
                </div>
                <div className="font-bold text-xl mb-2">
                  Hi! I'm Brimi 🌟
                </div>
                <p className="text-muted-foreground max-w-md mx-auto text-sm">
                  Ask me anything about{" "}
                  <b>{session?.topic_title}</b>. I'll explain concepts, work
                  through examples, and quiz you when you're ready.
                </p>
              </div>
            )}

            {messages.map((m, i) => (
              <div
                key={m.id}
                className={`flex gap-3 bubble-in ${m.role === "user" ? "justify-end" : ""}`}
                data-testid={`chat-msg-${m.role}`}
              >
                {m.role === "assistant" && (
                  <div className="w-8 h-8 rounded-xl bg-[hsl(var(--brimi-blue))]/15 text-[hsl(var(--brimi-blue))] flex items-center justify-center shrink-0">
                    <Bot className="w-4 h-4" />
                  </div>
                )}
                <div
                  className={`max-w-[85%] sm:max-w-[75%] px-4 py-3 rounded-2xl text-sm sm:text-base leading-relaxed ${
                    m.role === "user"
                      ? "bg-[hsl(var(--brimi-blue))] text-white rounded-tr-sm"
                      : "bg-muted text-foreground rounded-tl-sm border border-border"
                  }`}
                >
                  <div className="whitespace-pre-wrap">
                    {m.content ? (
                      renderMarkdown(m.content)
                    ) : (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    )}
                  </div>
                  {m.role === "assistant" && m.content && !streaming && (
                    <div className="mt-2 pt-2 border-t border-border/40 flex items-center justify-end">
                      <ReadAloudButton text={m.content} testId={`read-aloud-${i}`} />
                    </div>
                  )}
                </div>
                {m.role === "user" && (
                  <div className="w-8 h-8 rounded-xl bg-[hsl(var(--brimi-coral))] text-white flex items-center justify-center shrink-0">
                    <UserIcon className="w-4 h-4" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="border-t border-border p-4 pb-6 sm:pb-4 bg-card space-y-3">
            {editorOpen && (
              <CodeEditor
                defaultLanguage={playgroundLang}
                disabled={streaming}
                onClose={() => setEditorOpen(false)}
                onSend={(payload) => {
                  setEditorOpen(false);
                  sendMessage(payload);
                }}
              />
            )}
            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
              <textarea
                data-testid="chat-input"
                ref={chatInputRef}
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask Brimi anything about this chapter…"
                className="flex-1 resize-none rounded-2xl border border-border bg-background px-4 py-3 text-base outline-none focus:border-[hsl(var(--brimi-blue))] focus:ring-2 focus:ring-[hsl(var(--brimi-blue))]/20 max-h-32"
                disabled={streaming}
              />
              <div className="flex items-center justify-end gap-2">
                {showPlayground && (
                  <button
                    type="button"
                    onClick={() => setEditorOpen((v) => !v)}
                    data-testid="toggle-code-editor"
                    title={editorOpen ? "Close playground" : "Open code playground"}
                    className={`pill-btn px-3 py-3 ${
                      editorOpen
                        ? "bg-foreground/10 text-foreground"
                        : "bg-[hsl(var(--brimi-mint))]/30 hover:bg-[hsl(var(--brimi-mint))]/50 text-foreground"
                    }`}
                  >
                    <Code2 className="w-5 h-5" />
                  </button>
                )}
                <MathTools insertText={makeInsertHandler(chatInputRef, setInput)} />
                <MicButton
                  disabled={streaming}
                  onTranscribed={(text) =>
                    setInput((prev) => (prev ? `${prev} ${text}` : text))
                  }
                />
                <button
                  data-testid="chat-send-btn"
                  onClick={sendMessage}
                  disabled={streaming || !input.trim()}
                  className="pill-btn bg-[hsl(var(--brimi-blue))] hover:bg-[hsl(var(--brimi-blue))]/90 text-white px-5 py-3 disabled:opacity-40"
                >
                  {streaming ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
